import { ListItem } from 'src/app/components/scrolling-list';
import {
  FolderService,
  HasFolderStructure,
} from 'src/app/shared/services/folder.service';
import { FolderItem } from './folder';

export class FolderOperationService<
  T extends { id?: string; folderId?: string },
> {
  constructor(
    private folderService: FolderService,
    private folderStructureService: HasFolderStructure<T>,
  ) {}

  async executeSingleOperation(
    operation: (item: T | FolderItem) => Promise<any>,
    item: T | FolderItem,
  ): Promise<any> {
    if (!this.isFolderItem(item)) {
      return operation(item);
    }
    const items = await this.getAllItems(item.id);
    return Promise.all(items.map(operation));
  }

  async executeGroupOperation(
    operation: (selection: {
      items: (T | FolderItem)[];
      [key: string]: any;
    }) => Promise<any>,
    selection: { items: (T | FolderItem)[]; [key: string]: any },
  ): Promise<any> {
    const { items } = selection;
    const folders = items.filter(this.isFolderItem);

    if (folders.length === 0) {
      return operation(selection);
    }
    const subfolders = await Promise.all(
      folders.map((folder) => this.getAllItems(folder.id)),
    );
    const subfoldersItems = subfolders.flat();
    return operation({
      ...selection,
      items: [
        ...items.filter((item) => !this.isFolderItem(item)),
        ...subfoldersItems,
      ],
    });
  }

  async executeSingleBeforeBatchAction(
    operation: (item: T[]) => Promise<any>,
    selection: ListItem<T>[],
  ): Promise<any> {
    const folders = selection.filter((item) => item.isFolder);
    let items = [
      ...selection
        .filter((item) => !item.isFolder)
        .map((item) => item.item as T),
    ];
    if (folders.length > 0) {
      const subfolders = await Promise.all(
        folders.map((folder) => this.getAllItems(folder.item.id)),
      );
      items = [...(items as T[]), ...subfolders.flat()];
    }
    return operation(items);
  }

  private async getAllItems(folderId: string) {
    return this.folderService.getItemsFromFolderAndSubfolders(
      folderId,
      this.folderStructureService,
    );
  }

  private isFolderItem(item: T | FolderItem): item is FolderItem {
    return (item as FolderItem).isFolder === true;
  }
}
